import { useParams } from 'react-router-dom';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { Grid } from '@mui/material';
import { CerebroSecondaryButton, CerebroText } from '../../shared/components';
import { CompanySubscription, DealStatusView, ProductSubscription } from '../../shared/models';
import { CreateSubscriptionDrawer } from './CreateSubscriptionDrawer';
import React from 'react';
import { DeleteSubscriptionDialog } from './DeleteSubscriptionDialog';
import { UpdateDealSubscriptionDrawer } from './UpdateDealSubscriptionDrawer';
import { SubscriptionSuccessFeeTiersDrawer } from './SubscriptionSuccessFeeTiersDrawer';
import { ChangeProductDrawer } from './ChangeProductDrawer';
import { AssociateCompanySubscriptionDrawer } from './CompanySubscriptions/AssociateCompanySubscriptionDrawer';
import { CreateCompanySubscriptionDrawer } from './CompanySubscriptions/CreateCompanySubscriptionDrawer';
import { CompanySubscriptionSuccessFeeTiersDrawer } from './CompanySubscriptions/CompanySubscriptionSuccessFeeTiersDrawer';
import { DeleteCompanySubscriptionDialog } from './CompanySubscriptions/DeleteCompanySubscriptionDialog';
import { UploadSubscriptionAgreement } from './CompanySubscriptions/UploadSubscriptionAgreement';
import { CompanySubscriptionDisplay } from './CompanySubscriptions/CompanySubscriptionDisplay';
import { ProductSubscriptionDisplay } from './ProductSubscriptionDisplay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UploadAgreementDrawer } from './UploadAgreementDrawer';
import { CreateSuccessFeeInvoiceDrawer } from './CreateSuccessFeeInvoiceDrawer';
import { SubscriptionPaymentTermsDrawer } from './SubscriptionPaymentTermsDrawer';
import { CompanySubscriptionPaymentTermsDrawer } from './CompanySubscriptions/CompanySubscriptionPaymentTermsDrawer';
import { EditCompanySubscriptionDrawer } from './CompanySubscriptions/EditCompanySubscriptionDrawer';

interface DealSubscriptionsProps {
	dealStatus: DealStatusView | undefined;
	isClosedFunded: boolean;
}

export const DealSubscriptions: React.FC<DealSubscriptionsProps> = ({ dealStatus, isClosedFunded }) => {
	const { guid } = useParams<'guid'>();
	const createSubscription = dealStatus && dealStatus.deal;
	const canAssociationSubscription = createSubscription && dealStatus.companySubscriptions.length > 0;

	const [addSubscription, setAddSubscription] = React.useState<boolean>(false);
	const [editSubscription, setEditSubscription] = React.useState<ProductSubscription | null>(null);
	const [changeProduct, setChangeProduct] = React.useState<ProductSubscription | null>(null);
	const [tierSubscription, setTierSubscription] = React.useState<ProductSubscription | null>(null);
	const [tierCompanySubscription, setTierCompanySubscription] = React.useState<CompanySubscription | null>(null);
	const [companyPaymentTerms, setCompanyPaymentTerms] = React.useState<CompanySubscription | null>(null);
	const [productPaymentTerms, setProductPaymentTerms] = React.useState<ProductSubscription | null>(null);
	const [uploadCompanySubscriptionAgreement, setUploadCompanySubscriptionAgreement] =
		React.useState<CompanySubscription | null>(null);
	const [deleteSubscription, setDeleteSubscription] = React.useState<ProductSubscription | null>(null);
	const [assoicateSubscription, setAssociateSubscription] = React.useState<boolean>(false);
	const [addCompanySubscription, setAddCompanySubscription] = React.useState<boolean>(false);
	const [uploadSubscriptionAgreement, setUploadSubscriptionAgreement] = React.useState<ProductSubscription | null>(
		null
	);
	const [deleteCompanySubscription, setDeleteCompanySubscription] = React.useState<CompanySubscription | null>(null);
	const [createInvoice, setCreateInvoice] = React.useState<ProductSubscription | null>(null);
	const [editCompanySubscription, setEditCompanySubscription] = React.useState<CompanySubscription | null>(null);

	const onDrawersClose = () => {
		setAddSubscription(false);
		setEditSubscription(null);
		setChangeProduct(null);
		setTierSubscription(null);
		setDeleteSubscription(null);
		setAssociateSubscription(false);
		setAddCompanySubscription(false);
		setTierCompanySubscription(null);
		setDeleteCompanySubscription(null);
		setUploadSubscriptionAgreement(null);
		setUploadCompanySubscriptionAgreement(null);
		setCreateInvoice(null);
		setCompanyPaymentTerms(null);
		setProductPaymentTerms(null);
		setEditCompanySubscription(null);
	};

	return dealStatus && dealStatus.subscription ? (
		<>
			{dealStatus.companySubscription ? (
				<CompanySubscriptionDisplay
					subscription={dealStatus.companySubscription}
					companyGuid={dealStatus.deal.companyGuid}
					isClosedFunded={isClosedFunded}
					onEdit={setEditCompanySubscription}
					onEditSuccessTiers={setTierCompanySubscription}
					onUploadAgreement={setUploadCompanySubscriptionAgreement}
					onEditPaymentTerms={setCompanyPaymentTerms}
					onDelete={setDeleteCompanySubscription}
				/>
			) : (
				<ProductSubscriptionDisplay
					dealGuid={guid}
					subscription={dealStatus.subscription}
					deal={dealStatus.deal}
					product={dealStatus.product}
					isClosedFunded={isClosedFunded}
					onEdit={setEditSubscription}
					onEditSuccessTiers={setTierSubscription}
					onEditPaymentTerms={setProductPaymentTerms}
					onChangeProduct={setChangeProduct}
					onUpload={setUploadSubscriptionAgreement}
					onDelete={setDeleteSubscription}
					onCreateInvoice={setCreateInvoice}
				/>
			)}

			<DeleteSubscriptionDialog dealGuid={guid} subscription={deleteSubscription} close={onDrawersClose} />
			<DeleteCompanySubscriptionDialog
				dealGuid={guid}
				companyGuid={dealStatus.deal.companyGuid}
				subscription={deleteCompanySubscription}
				close={onDrawersClose}
			/>
			<UpdateDealSubscriptionDrawer
				subscription={editSubscription}
				deal={dealStatus.deal}
				onClose={onDrawersClose}
			/>
			<SubscriptionSuccessFeeTiersDrawer
				subscription={tierSubscription}
				deal={dealStatus.deal}
				onClose={onDrawersClose}
			/>
			<CompanySubscriptionSuccessFeeTiersDrawer
				subscription={tierCompanySubscription}
				deal={dealStatus.deal}
				onClose={onDrawersClose}
			/>
			<ChangeProductDrawer subscription={changeProduct} deal={dealStatus.deal} onClose={onDrawersClose} />
			<UploadSubscriptionAgreement
				dealGuid={guid}
				companyGuid={dealStatus.deal.companyGuid}
				subscription={uploadCompanySubscriptionAgreement}
				onClose={onDrawersClose}
			/>
			<UploadAgreementDrawer
				dealGuid={guid}
				subscription={uploadSubscriptionAgreement}
				onClose={onDrawersClose}
			/>
			<CreateSuccessFeeInvoiceDrawer
				subscription={createInvoice}
				deal={dealStatus.deal}
				onClose={onDrawersClose}
			/>

			<SubscriptionPaymentTermsDrawer
				onClose={onDrawersClose}
				subscription={productPaymentTerms}
				deal={dealStatus.deal}
			/>

			<CompanySubscriptionPaymentTermsDrawer
				onClose={onDrawersClose}
				subscription={companyPaymentTerms}
				deal={dealStatus.deal}
			/>

			<EditCompanySubscriptionDrawer
				subscription={editCompanySubscription}
				onClose={onDrawersClose}
				deal={dealStatus.deal}
			/>
		</>
	) : createSubscription ? (
		<>
			<Grid container sx={{ marginBottom: 2 }}>
				<Grid item xs={12}>
					{canAssociationSubscription ? (
						<>
							<CerebroText sx={{ marginBottom: 2, marginTop: 2 }}>
								<FontAwesomeIcon icon={faInfoCircle} size="sm" /> &nbsp; This company has a company
								subscription.
							</CerebroText>
							<CerebroSecondaryButton
								onClick={() => setAssociateSubscription(true)}
								sx={{ marginRight: 2 }}
							>
								Add Deal to Subscription
							</CerebroSecondaryButton>
						</>
					) : !dealStatus.deal.hasParentCompany ? (
						<CerebroSecondaryButton onClick={() => setAddCompanySubscription(true)} sx={{ marginRight: 2 }}>
							Create Company Subscription
						</CerebroSecondaryButton>
					) : (
						<></>
					)}
					{!dealStatus.deal.hasParentCompany && (
						<CerebroSecondaryButton onClick={() => setAddSubscription(true)}>
							Create Single Engagement
						</CerebroSecondaryButton>
					)}
				</Grid>
			</Grid>

			<CreateSubscriptionDrawer open={addSubscription} deal={dealStatus.deal} onClose={onDrawersClose} />
			<AssociateCompanySubscriptionDrawer
				open={assoicateSubscription}
				dealStatus={dealStatus}
				onClose={onDrawersClose}
			/>
			<CreateCompanySubscriptionDrawer
				open={addCompanySubscription}
				deal={dealStatus.deal}
				onClose={onDrawersClose}
			/>
		</>
	) : (
		<></>
	);
};
